// View-only, collapsible session-break section for the calendar sidebar "phone" preview.
// Modeled on the workouts module's WorkoutBreakSection / WorkoutsListBreak, but copied into
// the calendar module so the two never share components.
import React, { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import CalendarWorkoutListItem from './CalendarWorkoutListItem';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const SectionDiv = styled('div')`
  display: flex;
  flex-direction: column;
  border: 1px solid #34495E;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 15px;
  width: 100%;
  /* Keep the block's height when the panel is shortened; the scrolling
     ListWrapper (a flex column) would otherwise compress it. */
  flex-shrink: 0;
`;

const HeaderBar = styled('div')`
  display: flex;
  cursor: pointer;
  padding: 10px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background: #34495E;
`;

const IconContainer = styled('div')`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  width: 30px;
  height: 30px;
  min-width: 30px;
  background: ${(props) => (props.completed ? '#0dcc8a' : 'white')};
`;

const IconAndLine = styled('div')`
  display: flex;
  flex-direction: column;
  margin-right: 16px;
  align-items: center;
  justify-content: center;
`;

const GroupingLetterContainer = styled('div')`
  display: flex;
  align-items: center;
  border-radius: 50px;
  border-width: 1px;
  height: 15px;
  justify-content: center;
  padding: 1.5px;
  position: absolute;
  width: 15px;
  border: ${(props) => (!props.completed ? `1px solid ${props.borderColor}` : '#0dcc8a')};
  background: ${(props) => props.borderColor};
  right: -3px;
  bottom: -3px;
`;

const GroupingLetter = styled('div')`
  color: ${(props) => props.color};
  font-size: 9px;
  font-weight: 500;
`;

const IconTitle = styled('div')`
  display: flex;
  max-width: 100%;
  width: 100%;
  color: white;
  align-items: center;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  width: 100%;
  padding-right: 15px;
`;

const ChevronContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const Chevron = styled('div')`
  transform: ${(props) => (props.isRotated ? 'rotate(270deg)' : 'rotate(90deg)')};
  transition: transform 0.5s ease;
  display: flex;
  justify-content: center;
`;

const ItemNumberContainer = styled('div')`
  height: 50px;
  display: flex;
  align-items: center;
`;

const ItemNumber = styled('div')`
  display: flex;
  width: 60px;
  justify-content: center;
  border: 1px solid grey;
  margin-left: 10px;
  border-radius: 10px;
  font-size: 11px;
  font-family: 'Nunito Sans';
  color: grey;
`;

const ExpandSection = styled('div')`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: ${(props) => (props.expand ? `${props.heightCalc}px` : '0px')};
  transition: max-height 0.5s ease;
  margin-left: 15px;
`;

const CalendarSessionBreakSection = ({ item, sectionChildren, onSelectExercise }) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(!!item.showSessionBreakItems);
  const count = sectionChildren.length;

  return (
    <SectionDiv>
      <HeaderBar onClick={() => setIsExpanded(!isExpanded)}>
        <IconAndLine>
          <IconContainer borderColor={item?.groupingColorCode || 'white'}>
            <IcomoonReact
              iconSet={iconSet}
              size={15}
              icon={item.icon}
              color='black'
            />
            {item.groupingColorCode && item.groupingLetter && (
              <GroupingLetterContainer borderColor={item?.groupingColorCode || 'black'}>
                <GroupingLetter color={theme.colors.background[0]}>
                  {item.groupingLetter}
                </GroupingLetter>
              </GroupingLetterContainer>
            )}
          </IconContainer>
        </IconAndLine>
        <IconTitle>
          <Title>{item.title}</Title>
        </IconTitle>
        <ChevronContainer>
          <Chevron isRotated={isExpanded}>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon='altrightarrow'
              color='white'
            />
          </Chevron>
        </ChevronContainer>
      </HeaderBar>
      <ItemNumberContainer>
        <ItemNumber>{count > 1 ? `${count} items` : `${count} item`}</ItemNumber>
      </ItemNumberContainer>
      <ExpandSection heightCalc={count * 55} expand={isExpanded}>
        {sectionChildren.map((child, idx) => (
          <CalendarWorkoutListItem
            key={child.item.assignedId || child.item.saveDataId || child.flatIndex}
            workoutItem={child.item}
            showVerticalLine={idx < count - 1}
            onSelect={() => onSelectExercise(child.flatIndex)}
          />
        ))}
      </ExpandSection>
    </SectionDiv>
  );
};

CalendarSessionBreakSection.propTypes = {
  item: PropTypes.instanceOf(Object).isRequired,
  sectionChildren: PropTypes.arrayOf(PropTypes.shape({
    kind: PropTypes.string,
    item: PropTypes.instanceOf(Object),
    flatIndex: PropTypes.number,
  })).isRequired,
  onSelectExercise: PropTypes.func.isRequired,
};

export default CalendarSessionBreakSection;
